import { useRef, useEffect, useState } from 'react';

interface VideoSectionProps {
  src: string;
  headline?: string;
  body?: string;
  zIndex?: number;
}

function useVideoAutoplay(ref: React.RefObject<HTMLVideoElement | null>) {
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const play = () => video.play().catch(() => {});
    play();
    const onVisible = () => { if (!document.hidden) play(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const fn = () => setIsMobile(mql.matches);
    mql.addEventListener('change', fn);
    fn();
    return () => mql.removeEventListener('change', fn);
  }, []);
  return isMobile;
}

/** Same split layout and scroll effect as SplitSection; no borders on video. */
export default function VideoSection({
  src,
  headline = 'Movement in motion.',
  body,
  zIndex = 42
}: VideoSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile();
  const [isInView, setIsInView] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useVideoAutoplay(videoRef);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.2 }
    );
    observer.observe(section);

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (rect.top < windowHeight && rect.bottom > 0) {
        const progress = Math.min(1, Math.max(0, (windowHeight - rect.top) / (windowHeight + rect.height)));
        setScrollProgress(progress);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const entranceProgress = Math.min(1, scrollProgress * 3);
  const exitProgress = Math.max(0, (scrollProgress - 0.6) * 2.5);
  const imageX = (1 - entranceProgress) * -60 + exitProgress * -18;
  const panelX = (1 - entranceProgress) * 44 + exitProgress * 10;
  const contentOpacity = entranceProgress * (1 - exitProgress);
  const contentY = (1 - entranceProgress) * 10 - exitProgress * 8;

  const copyContent = (
    <>
      <h2 className={`vyera-heading-lg text-[var(--vyera-text-primary)] mb-6 md:mb-8 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {headline}
      </h2>
      {body && (
        <p className={`vyera-body text-[var(--vyera-text-secondary)] transition-all duration-700 delay-100 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {body}
        </p>
      )}
    </>
  );

  if (isMobile) {
    return (
      <section ref={sectionRef} className="relative w-full min-h-0 flex flex-col overflow-hidden" style={{ zIndex }}>
        <div className="w-full aspect-[9/16] max-h-[70vh] flex-shrink-0 bg-[var(--vyera-cream)] flex items-center justify-center overflow-hidden">
          <video ref={videoRef} src={src} className="h-full w-auto object-contain outline-none border-0" muted loop playsInline aria-label="VYERA movement" />
        </div>
        <div className="flex-1 min-h-[28vh] bg-[var(--vyera-cream)] flex items-center px-6 py-10 pb-safe">
          <div className="w-full max-w-md">{copyContent}</div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="relative w-full min-h-screen flex overflow-hidden" style={{ zIndex }}>
      <div
        className="absolute left-0 top-0 w-[56vw] h-full bg-[var(--vyera-cream)] flex items-center justify-center"
        style={{ transform: `translateX(${imageX}vw) scale(${1 + (1 - entranceProgress) * 0.08})`, opacity: contentOpacity }}
      >
        <video ref={videoRef} src={src} className="max-h-[85vh] w-auto object-contain outline-none border-0" muted loop playsInline aria-label="VYERA movement" />
      </div>
      <div className="absolute right-0 top-0 w-[44vw] h-full bg-[var(--vyera-cream)] flex items-center" style={{ transform: `translateX(${panelX}vw)`, opacity: contentOpacity }}>
        <div className="px-[6vw] max-w-[30vw]" style={{ transform: `translateY(${contentY}vh)`, opacity: contentOpacity }}>
          {copyContent}
        </div>
      </div>
    </section>
  );
}
